import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  TrackByFunction,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { RxFor } from '@rx-angular/template/for';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  AccurateDateIntervalWithStay,
  FeatureName,
  RoleAction,
  RolePermissionSubject,
  StaffLayoutStyle,
  Stay,
  STAYS_TIMELINE_ADJUSTMENT_PIXEL,
} from '@wilson/interfaces';
import {
  ACTION_TOASTER_DURATION,
  NzMessageTimerLineComponent,
} from '@wilson/non-domain-specific/ng-zorro/nz-message';
import { StaysDrawerStateService } from '@wilson/non-domain-specific/stays-form-and-drawer';
import {
  DeferredStayCardHelperService,
  GanttEndingPixelPipe,
  GanttStartingPixelPipe,
  GroupItemsSelectionHelperService,
  MessageWithUndoOptionComponent,
  ShiftMultiSelectHelperService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import { ShiftTimelinePipesModule } from '@wilson/shift-timeline/pipes';
import {
  ChangedKeyManagerService,
  DragAndDropModeService,
  PreferredSettingsStateFacadeService,
} from '@wilson/shift-timeline/services';
import {
  ShiftTimelineDataState,
  TimelineStaysSuggestionService,
} from '@wilson/shift-timeline/state';
import { Observable } from 'rxjs';

@Component({
  selector: 'wilson-timeline-user-stays',
  imports: [
    CommonModule,
    FeatureFlagsModule,
    GanttStartingPixelPipe,
    GanttEndingPixelPipe,
    ShiftTimelinePipesModule,
    TranslateModule,
    NzMessageTimerLineComponent,
    MessageWithUndoOptionComponent,
    AuthorizationModule,
    RxFor,
  ],
  templateUrl: './timeline-user-stays.component.html',
  styleUrl: './timeline-user-stays.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimelineUserStaysComponent implements OnInit {
  readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly changedKeyManagerService = inject(ChangedKeyManagerService);
  readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  private readonly groupItemsSelectionHelperService = inject(
    GroupItemsSelectionHelperService,
  );
  private readonly staysDrawerStateService = inject(StaysDrawerStateService);
  private readonly store = inject(Store);
  protected readonly timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  protected readonly deferredStayCardHelperService = inject(
    DeferredStayCardHelperService,
  );
  private readonly preferredSettingsStateFacadeService = inject(
    PreferredSettingsStateFacadeService,
  );

  public readonly MESSAGE_DURATION = ACTION_TOASTER_DURATION;
  @Input({ required: true }) stayViews!: AccurateDateIntervalWithStay[];
  @Input({ required: true }) itemHeight!: number;
  @Input() isOverlapDetected = false;
  @Output() openEditStayDrawer = new EventEmitter();

  trackByStayDetail: TrackByFunction<AccurateDateIntervalWithStay> = (
    _: number,
    accurateDateIntervalWithStay: AccurateDateIntervalWithStay,
  ) => {
    return this.changedKeyManagerService.computeChangedKeyForStay(
      accurateDateIntervalWithStay.stay,
    );
  };

  public stayIdsToHighlight$ = this.store.select(
    ShiftTimelineDataState.stayIdsToHighlight,
  );

  STAYS_TIMELINE_ADJUSTMENT_PIXEL = STAYS_TIMELINE_ADJUSTMENT_PIXEL;
  FeatureName = FeatureName;
  RoleAction = RoleAction;
  RolePermissionSubject = RolePermissionSubject;
  staffLayoutStyle$!: Observable<StaffLayoutStyle>;
  StaffLayoutStyle = StaffLayoutStyle;

  ngOnInit(): void {
    this.staffLayoutStyle$ =
      this.preferredSettingsStateFacadeService.staffLayoutStyle$;
  }

  protected async selectStay(
    data: {
      stay: Stay;
      left: number | null;
      right: number | null;
    },
    undoTemplate: TemplateRef<void>,
  ): Promise<void> {
    this.shiftMultiSelectHelperService.clearSelectionWithUndo(
      undoTemplate,
      this.MESSAGE_DURATION,
    );
    await this.groupItemsSelectionHelperService.selectGroupItems(data.stay.id);
    this.openEditStayDrawer.emit({
      left: data.left,
      right: data.right,
      stay: data.stay,
    });
  }

  onUndo(): void {
    this.staysDrawerStateService.setEditStaysDrawerVisibility(false);
    this.groupItemsSelectionHelperService.clearSelectedItems();
  }
}
